import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  useReactTable,
  getCoreRowModel,
  type SortingState,
  type PaginationState,
} from "@tanstack/react-table";
import { DataTable, DataTablePagination, TableSkeleton } from "@repo/ui";
import { createDispenseColumns } from "./dispense-columns";
import {
  DispenseFilters,
  type DispenseFilters as DispenseFiltersType,
} from "./dispense-filters";
import type { Dispense } from "@/lib/types/inventory";
import { usePaginatedData } from "@/hooks";
import { DrugDetailsDrawer } from "@/components/inventory/drug-details-drawer";

export interface DispenseHistoryTableProps {
  onView: (dispense: Dispense) => void;
  onEdit?: (dispense: Dispense) => void;
  onDelete?: (dispense: Dispense) => void;
  onEditNotes?: (dispense: Dispense) => void;
  refreshTrigger?: number;
}

export function DispenseHistoryTable({
  onView,
  onEdit,
  onDelete,
  onEditNotes,
  refreshTrigger,
}: DispenseHistoryTableProps) {
  const navigate = useNavigate();
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const searchParams = React.useMemo(
    () => new URLSearchParams(searchStr),
    [searchStr]
  );

  // Initialize filters from URL params
  const initialFilters = React.useMemo(() => {
    const filters: DispenseFiltersType = {};
    if (searchParams.get("dateFrom")) {
      filters.dateFrom = searchParams.get("dateFrom") || undefined;
    }
    if (searchParams.get("dateTo")) {
      filters.dateTo = searchParams.get("dateTo") || undefined;
    }
    if (searchParams.get("userIds")) {
      filters.userIds = searchParams.get("userIds") || undefined;
    }
    if (searchParams.get("ndc")) {
      filters.ndc = searchParams.get("ndc") || undefined;
    }
    if (searchParams.get("itemName")) {
      filters.itemName = searchParams.get("itemName") || undefined;
    }
    if (searchParams.get("rxNumber")) {
      filters.rxNumber = searchParams.get("rxNumber") || undefined;
    }
    if (searchParams.get("patientId")) {
      filters.patientId = searchParams.get("patientId") || undefined;
    }
    if (searchParams.get("patientName")) {
      filters.patientName = searchParams.get("patientName") || undefined;
    }
    return filters;
  }, [searchParams]);

  const [filters, setFilters] =
    React.useState<DispenseFiltersType>(initialFilters);

  // Convert filters to Record<string, string> for the hook (exclude display-only keys)
  const filterRecord = React.useMemo<Record<string, string>>(() => {
    const record: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== "patientName") {
        record[key] = value;
      }
    });
    return record;
  }, [filters]);

  // Server-side pagination hook
  const {
    data,
    pagination: paginationInfo,
    isLoading,
    error,
    setPage,
    setPageSize,
    setSorting: setServerSorting,
    setFilters: setServerFilters,
    refetch,
  } = usePaginatedData<Dispense>({
    endpoint: "/dispenses",
    defaultPageSize: 50,
    defaultSortBy: "date",
    defaultSortOrder: "desc",
    filters: filterRecord,
  });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [drugDetailsDrawerOpen, setDrugDetailsDrawerOpen] =
    React.useState(false);
  const [selectedNDC, setSelectedNDC] = React.useState<string | null>(null);

  const handleNDCClick = (ndc: string) => {
    setSelectedNDC(ndc);
    setDrugDetailsDrawerOpen(true);
  };

  const handleAddToReminder = (dispense: Dispense) => {
    // item_name may be flattened from the API or nested in inventory
    const itemName =
      (dispense as Dispense & { item_name?: string }).item_name ||
      dispense.inventory?.item_name ||
      dispense.ndc;
    navigate({
      to: `/reminders?action=create&referenceType=dispense&referenceId=${encodeURIComponent(dispense.id)}&referenceName=${encodeURIComponent(itemName)}`,
    });
  };

  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 50,
    });

  const columns = React.useMemo(
    () =>
      createDispenseColumns(
        onView,
        onEdit,
        onDelete,
        onEditNotes,
        handleNDCClick,
        onEdit || onDelete ? handleAddToReminder : undefined
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onView, onEdit, onDelete, onEditNotes]
  );

  // Handle sorting changes — skip on mount (empty initial sorting state)
  const sortingMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!sortingMountedRef.current) {
      sortingMountedRef.current = true;
      return;
    }
    if (sorting.length > 0) {
      const sort = sorting[0];
      setServerSorting(sort.id, sort.desc ? "desc" : "asc");
    }
  }, [sorting, setServerSorting]);

  // Trigger refetch when refreshTrigger changes
  React.useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Handle filter changes and update URL
  const handleFilterChange = React.useCallback(
    (newFilters: DispenseFiltersType) => {
      setFilters(newFilters);

      // Convert to Record<string, string> for the hook
      const record: Record<string, string> = {};
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          record[key] = value;
        }
      });
      setServerFilters(record);

      // Update URL params
      const params = new URLSearchParams(searchParams.toString());

      // Remove all filter params first
      params.delete("dateFrom");
      params.delete("dateTo");
      params.delete("userIds");
      params.delete("ndc");
      params.delete("itemName");
      params.delete("rxNumber");
      params.delete("patientId");
      params.delete("patientName");

      // Add new filter params
      if (newFilters.dateFrom) {
        params.set("dateFrom", newFilters.dateFrom);
      }
      if (newFilters.dateTo) {
        params.set("dateTo", newFilters.dateTo);
      }
      if (newFilters.userIds) {
        params.set("userIds", newFilters.userIds);
      }
      if (newFilters.ndc) {
        params.set("ndc", newFilters.ndc);
      }
      if (newFilters.itemName) {
        params.set("itemName", newFilters.itemName);
      }
      if (newFilters.rxNumber) {
        params.set("rxNumber", newFilters.rxNumber);
      }
      if (newFilters.patientId) {
        params.set("patientId", newFilters.patientId);
      }
      if (newFilters.patientName) {
        params.set("patientName", newFilters.patientName);
      }

      navigate({ to: `/dispenses?${params.toString()}`, replace: true });
    },
    [setServerFilters, searchParams, navigate]
  );

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: paginationInfo.totalPages,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(pagination) : updater;
      setPagination(next);
      if (next.pageIndex !== pagination.pageIndex) {
        setPage(next.pageIndex + 1);
      }
      if (next.pageSize !== pagination.pageSize) {
        setPageSize(next.pageSize);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== ""
  );
  const hasData = data.length > 0;
  const showEmptyState = !isLoading && !hasData;

  return (
    <div className="space-y-4" data-testid="dispense-list">
      <DispenseFilters
        onFilterChange={handleFilterChange}
        initialFilters={filters}
      />

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {showEmptyState && !error && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">
            No dispenses found
          </p>
          {hasActiveFilters && (
            <p className="mt-2 text-sm text-muted-foreground">
              Try adjusting your filters to see more results
            </p>
          )}
        </div>
      )}

      {hasData && (
        <>
          <DataTable
            table={table}
            minWidth="1100px"
            getRowTestId={(row) =>
              row.original.rx_number
                ? `dispense-row-${row.original.rx_number}`
                : undefined
            }
          />
          <DataTablePagination table={table} />
        </>
      )}

      {isLoading && !hasData && <TableSkeleton columns={9} rows={10} />}

      {selectedNDC && (
        <DrugDetailsDrawer
          open={drugDetailsDrawerOpen}
          onOpenChange={setDrugDetailsDrawerOpen}
          ndc={selectedNDC}
          mode="view"
        />
      )}
    </div>
  );
}
